import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { logAccess } from '@/lib/accessLogs';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import PrintSingleDocumentModal from '@/components/feature/PrintSingleDocumentModal';
import PrintableFicheVisite, {
  type FicheVisitePlainte,
  type FicheVisiteSoin,
  type FicheVisiteBilanLabo,
} from '@/components/feature/PrintableFicheVisite';

interface PatientInfo {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string;
  sexe: string;
  numero_dossier: string | null;
  groupe_sanguin: string | null;
  allergies: string | null;
  maladies_chroniques: string | null;
  antecedents_medicaux: string | null;
  antecedents_chirurgicaux: string | null;
  medicaments_actuels: string | null;
  personne_contact_nom: string | null;
  personne_contact_telephone: string | null;
}

interface ConsultationEntry {
  id: string;
  date_consultation: string;
  heure_debut: string | null;
  heure_fin: string | null;
  poids: number | null;
  taille: number | null;
  temperature: number | null;
  tension: string | null;
  saturation: number | null;
  symptomes_signes: string | null;
  examen: string | null;
  diagnostic: string | null;
  traitement_en_cours: string | null;
  suivi_vers: string | null;
  mot_fin: string | null;
  statut: string;
  medecin_nom: string;
  medecin_prenom: string;
  medecin_specialite: string | null;
}

interface SuiteItem {
  id: string;
  statut: string;
  created_at: string;
  plaintes: { date_plainte: string; description: string; symptomes: string | null }[];
  sessions: { date_soin: string; description: string }[];
}

interface FicheSoinItem {
  id: string;
  date_soin: string;
  soins_a_administrer: string;
  statut: string | null;
}

interface BonLaboItem {
  id: string;
  type_analyse: string;
  resultats: string | null;
  date_resultat: string | null;
  date_prescription: string | null;
}

interface DossierAnalyseItem {
  id: string;
  bon_laboratoire_id: string | null;
  date_analyse: string;
  conclusion: string | null;
}

function formatDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}

function formatHeure(t: string | null) {
  if (!t) return '';
  return t.substring(0, 5);
}

function statutBadgeClass(s: string) {
  switch (s) {
    case 'termine':
    case 'Terminée':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'en_cours':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
}

const cardClass = 'bg-white rounded-2xl border border-gray-200 overflow-hidden';

export default function FicheVisitePage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId') || '';
  const consultationId = searchParams.get('consultationId') || '';

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [patient, setPatient] = useState<PatientInfo | null>(null);
  const [consultation, setConsultation] = useState<ConsultationEntry | null>(null);
  const [suites, setSuites] = useState<SuiteItem[]>([]);
  const [fichesSoins, setFichesSoins] = useState<FicheSoinItem[]>([]);
  const [bonsLabo, setBonsLabo] = useState<BonLaboItem[]>([]);
  const [dossiersAnalyses, setDossiersAnalyses] = useState<DossierAnalyseItem[]>([]);

  const [printOpen, setPrintOpen] = useState(false);
  const [medecinInfo, setMedecinInfo] = useState<any>(null);

  const loadMedecinInfo = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('medecins')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    if (data) setMedecinInfo(data);
  }, [user]);

  const loadData = useCallback(async () => {
    if (!patientId || !consultationId) {
      setErrorMsg('Patient ou consultation manquant.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMsg('');
    try {
      // Patient
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('id, nom, prenom, date_naissance, sexe, numero_dossier, groupe_sanguin, allergies, maladies_chroniques, antecedents_medicaux, antecedents_chirurgicaux, medicaments_actuels, personne_contact_nom, personne_contact_telephone')
        .eq('id', patientId)
        .single();
      if (patientError) throw patientError;
      setPatient(patientData);

      // Consultation
      const { data: consData, error: consError } = await supabase
        .from('consultations')
        .select('*, medecins(nom, prenom, specialite)')
        .eq('id', consultationId)
        .single();
      if (consError) throw consError;
      if (!consData) throw new Error('Consultation introuvable.');

      setConsultation({
        id: consData.id,
        date_consultation: consData.date_consultation,
        heure_debut: consData.heure_debut,
        heure_fin: consData.heure_fin,
        poids: consData.poids,
        taille: consData.taille,
        temperature: consData.temperature,
        tension: consData.tension,
        saturation: consData.saturation,
        symptomes_signes: consData.symptomes_signes,
        examen: consData.examen,
        diagnostic: consData.diagnostic,
        traitement_en_cours: consData.traitement_en_cours,
        suivi_vers: consData.suivi_vers,
        mot_fin: consData.mot_fin,
        statut: consData.statut,
        medecin_nom: consData.medecins?.nom || '',
        medecin_prenom: consData.medecins?.prenom || '',
        medecin_specialite: consData.medecins?.specialite || null,
      });

      // Log
      if (user && role) {
        await logAccess({
          userId: user.id,
          userRole: role,
          actionType: 'VIEW',
          resourceType: 'FICHE_VISITE',
          resourceId: consultationId,
          patientId,
          details: {
            patient_nom: `${patientData.prenom} ${patientData.nom}`,
            action: 'Consultation de la fiche de visite',
            consultation_id: consultationId,
          },
        });
      }

      // Suites de soins liées à cette consultation
      const { data: suitesData } = await supabase
        .from('suites_soins')
        .select('id, statut, created_at')
        .eq('consultation_id', consultationId)
        .order('created_at', { ascending: false });
      const suitesRaw = (suitesData || []) as SuiteItem[];

      let enrichedSuites: SuiteItem[] = [];
      if (suitesRaw.length > 0) {
        const suiteIds = suitesRaw.map((s) => s.id);
        const [{ data: plaintesData }, { data: sessionsData }] = await Promise.all([
          supabase.from('plaintes_suivi').select('*').in('suite_soin_id', suiteIds),
          supabase.from('sessions_soins').select('*').in('suite_soin_id', suiteIds),
        ]);
        enrichedSuites = suitesRaw.map((s) => ({
          ...s,
          plaintes: (plaintesData || []).filter((p: any) => p.suite_soin_id === s.id),
          sessions: (sessionsData || []).filter((sess: any) => sess.suite_soin_id === s.id),
        }));
      }
      setSuites(enrichedSuites);

      // Fiches de soins liées
      const { data: fichesData } = await supabase
        .from('fiches_soins')
        .select('id, date_soin, soins_a_administrer, statut')
        .eq('consultation_id', consultationId)
        .order('date_soin', { ascending: false });
      setFichesSoins((fichesData || []) as FicheSoinItem[]);

      // Bons de labo liés
      const { data: bonsData } = await supabase
        .from('bons_laboratoire')
        .select('id, type_analyse, resultats, date_resultat, date_prescription')
        .eq('consultation_id', consultationId)
        .order('date_prescription', { ascending: false });
      const bons = (bonsData || []) as BonLaboItem[];
      setBonsLabo(bons);

      // Dossiers d'analyses liés aux bons
      if (bons.length > 0) {
        const bonIds = bons.map((b) => b.id);
        const { data: dossiersData } = await supabase
          .from('dossiers_analyses')
          .select('id, bon_laboratoire_id, date_analyse, conclusion')
          .in('bon_laboratoire_id', bonIds)
          .order('date_analyse', { ascending: false });
        setDossiersAnalyses((dossiersData || []) as DossierAnalyseItem[]);
      } else {
        setDossiersAnalyses([]);
      }
    } catch (err: any) {
      console.error('Erreur chargement fiche de visite:', err);
      setErrorMsg(err.message || 'Erreur lors du chargement des données.');
    } finally {
      setLoading(false);
    }
  }, [patientId, consultationId, user, role]);

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'medecin' && role !== 'super_admin') { navigate('/', { replace: true }); return; }
    if (!patientId || !consultationId) { navigate('/medecin/rendez-vous', { replace: true }); return; }
    loadMedecinInfo();
    loadData();
  }, [user, role, patientId, consultationId, navigate, loadMedecinInfo, loadData]);

  const hospitalisationActive = useMemo(() => {
    if (!consultation) return false;
    if (consultation.statut !== 'termine') return true;
    const suiteEnCours = suites.some((s) => s.statut === 'en_cours');
    return suiteEnCours;
  }, [consultation, suites]);

  const plaintes: FicheVisitePlainte[] = useMemo(() => {
    const list: FicheVisitePlainte[] = [];
    // Plaintes de la consultation d'entrée
    if (consultation?.symptomes_signes) {
      list.push({
        date: consultation.date_consultation,
        description: consultation.symptomes_signes,
        source: 'Admission',
      });
    }
    // Plaintes des suites
    suites.forEach((s) => {
      s.plaintes.forEach((p) => {
        list.push({
          date: p.date_plainte,
          description: [p.description, p.symptomes].filter(Boolean).join(' — '),
          source: 'Suivi',
        });
      });
    });
    return list;
  }, [consultation, suites]);

  const soins: FicheVisiteSoin[] = useMemo(() => {
    const list: FicheVisiteSoin[] = [];
    fichesSoins.forEach((f) => {
      list.push({
        date: f.date_soin,
        description: f.soins_a_administrer,
        source: 'Fiche de soins',
        statut: f.statut || '',
      });
    });
    suites.forEach((s) => {
      s.sessions.forEach((sess) => {
        list.push({
          date: sess.date_soin,
          description: sess.description,
          source: 'Suite de soins',
          statut: '',
        });
      });
    });
    return list;
  }, [fichesSoins, suites]);

  const bilanLabo: FicheVisiteBilanLabo[] = useMemo(() => {
    const bonIds = new Set(bonsLabo.map((b) => b.id));
    const list: FicheVisiteBilanLabo[] = [
      ...bonsLabo.map((b) => ({
        analyses: b.type_analyse || 'Analyse',
        resultats: b.resultats,
        date: b.date_resultat || b.date_prescription || '',
        source: 'Prescription' as const,
      })),
      ...dossiersAnalyses
        .filter((d) => !d.bon_laboratoire_id || bonIds.has(d.bon_laboratoire_id))
        .map((d) => ({
          analyses: 'Résultat',
          resultats: d.conclusion,
          date: d.date_analyse,
          source: 'Résultat' as const,
        })),
    ];
    return list;
  }, [bonsLabo, dossiersAnalyses]);

  const practitionerName = medecinInfo
    ? `${medecinInfo.prenom || ''} ${medecinInfo.nom || ''}`.trim()
    : (user?.user_metadata?.full_name || user?.user_metadata?.prenom || '');
  const practitionerTitle = medecinInfo?.specialite || '';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin"></i>
          <p className="mt-4 text-gray-600">Préparation de la fiche de visite...</p>
        </div>
      </div>
    );
  }

  if (!patient || !consultation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-red-500"></i>
          <p className="mt-4 text-xl font-semibold">Consultation introuvable</p>
          <button
            onClick={() => navigate('/medecin/rendez-vous')}
            className="mt-4 px-6 py-2 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue-dark cursor-pointer whitespace-nowrap"
          >
            Retour aux rendez-vous
          </button>
        </div>
      </div>
    );
  }

  const age = new Date().getFullYear() - new Date(patient.date_naissance).getFullYear();
  const entryDate = formatDate(consultation.date_consultation);
  const entryHeure = formatHeure(consultation.heure_debut);

  const patientHeader = `${patient.prenom} ${patient.nom}, ${age} ans · ${patient.sexe === 'M' ? 'Masculin' : 'Féminin'} · Dossier ${patient.numero_dossier || 'N/A'}`;

  const constantes = [
    { label: 'Poids', value: consultation.poids != null ? `${consultation.poids} kg` : '', icon: 'ri-scales-3-line' },
    { label: 'Taille', value: consultation.taille != null ? `${consultation.taille} cm` : '', icon: 'ri-ruler-line' },
    { label: 'Temp.', value: consultation.temperature != null ? `${consultation.temperature} °C` : '', icon: 'ri-temp-hot-line' },
    { label: 'Saturation', value: consultation.saturation != null ? `${consultation.saturation} %` : '', icon: 'ri-heart-pulse-line' },
    { label: 'Tension', value: consultation.tension || '', icon: 'ri-pulse-line' },
  ].filter((c) => c.value);

  const hasData = plaintes.length > 0 || soins.length > 0 || bilanLabo.length > 0 || !!consultation.diagnostic;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Sticky bar */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-arrow-left-line text-xl text-gray-600"></i>
              </button>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl font-bold text-gray-900 truncate">
                    Fiche de visite
                  </h1>
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold border ${hospitalisationActive ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-green-100 text-green-700 border-green-200'}`}>
                    {hospitalisationActive ? 'Hospitalisation en cours' : 'Hospitalisation terminée'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 truncate">{patientHeader}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setPrintOpen(true)}
                disabled={!hasData}
                className="px-5 py-2.5 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue-dark transition-all font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                                Imprimer
              </button>
            </div>
          </div>

          {errorMsg && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
              <i className="ri-error-warning-line"></i>
              {errorMsg}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Bandeau identité + constantes */}
        <div className={cardClass + ' mb-6'}>
          <div className="p-5 border-b border-gray-100 bg-clinic-blue/5">
            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {patient.prenom} {patient.nom}
                </h2>
                <p className="text-sm text-gray-600">
                  {age} ans · {patient.sexe === 'M' ? 'Masculin' : 'Féminin'} · N° dossier {patient.numero_dossier || 'N/A'}
                  {patient.groupe_sanguin ? ` · GS ${patient.groupe_sanguin}` : ''}
                </p>
              </div>
              {patient.allergies && (
                <div className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 font-medium">
                  <i className="ri-error-warning-line"></i>
                  Allergies : {patient.allergies}
                </div>
              )}
            </div>
          </div>

          <div className="p-5">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Colonne 1 : Admission */}
              <div>
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                                    Admission
                </h3>
                <div className="space-y-2 text-sm text-gray-800">
                  <p>
                    <span className="font-medium text-gray-500">Date :</span>{' '}
                    {entryDate}
                    {entryHeure ? ` à ${entryHeure}` : ''}
                  </p>
                  <p>
                    <span className="font-medium text-gray-500">Médecin :</span>{' '}
                    Dr {consultation.medecin_nom} {consultation.medecin_prenom}
                    {consultation.medecin_specialite ? ` · ${consultation.medecin_specialite}` : ''}
                  </p>
                  {consultation.suivi_vers && (
                    <p><span className="font-medium text-gray-500">Orientation :</span> {consultation.suivi_vers}</p>
                  )}
                  {consultation.mot_fin && (
                    <p><span className="font-medium text-gray-500">Mot de fin :</span> <span className="italic">{consultation.mot_fin}</span></p>
                  )}
                  <p>
                    <span className="font-medium text-gray-500">Statut consultation :</span>{' '}
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-semibold border ${statutBadgeClass(consultation.statut)}`}>
                      {consultation.statut === 'termine' ? 'Terminée' : consultation.statut === 'en_cours' ? 'En cours' : consultation.statut}
                    </span>
                  </p>
                </div>
              </div>

              {/* Colonne 2 : Constantes */}
              <div>
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                                    Constantes à l'admission
                </h3>
                {constantes.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">Aucune constante enregistrée.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {constantes.map((c) => (
                      <div key={c.label} className="p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                        <p className="flex items-center gap-1 text-[11px] text-gray-500 font-semibold">
                                                    {c.label}
                        </p>
                        <p className="text-sm font-bold text-gray-900 mt-0.5">{c.value}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Colonne 3 : Antécédents */}
              <div>
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                                    Antécédents
                </h3>
                <div className="space-y-1.5 text-sm">
                  {patient.maladies_chroniques && (
                    <p><span className="font-medium text-gray-500">Chroniques :</span> {patient.maladies_chroniques}</p>
                  )}
                  {patient.antecedents_medicaux && (
                    <p><span className="font-medium text-gray-500">Médicaux :</span> {patient.antecedents_medicaux}</p>
                  )}
                  {patient.antecedents_chirurgicaux && (
                    <p><span className="font-medium text-gray-500">Chirurgicaux :</span> {patient.antecedents_chirurgicaux}</p>
                  )}
                  {patient.medicaments_actuels && (
                    <p><span className="font-medium text-gray-500">Médicaments :</span> {patient.medicaments_actuels}</p>
                  )}
                  {!patient.maladies_chroniques && !patient.antecedents_medicaux && !patient.antecedents_chirurgicaux && !patient.medicaments_actuels && (
                    <p className="text-gray-400 italic">Aucun antécédent renseigné.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions rapides (visible seulement si hospitalisation active) */}
        {hospitalisationActive && (
          <div className="flex flex-wrap gap-3 mb-6">
            <button
              onClick={() => navigate(`/medecin/suite-soins?consultationId=${consultationId}`)}
              className="px-4 py-2.5 bg-clinic-blue text-white rounded-xl text-sm font-semibold hover:bg-clinic-blue-dark transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
                            Continuer les suites de soins
            </button>
            <button
              onClick={() => navigate(`/medecin/bon-laboratoire?patientId=${patientId}&consultationId=${consultationId}`)}
              className="px-4 py-2.5 bg-white border-2 border-clinic-blue/30 text-clinic-blue-dark rounded-xl text-sm font-semibold hover:bg-clinic-blue/10 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
                            Nouveau bilan labo
            </button>
          </div>
        )}

        {/* Grid : Plaintes / Soins / Labo / Diagnostic */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Plaintes */}
          <div className={cardClass}>
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-gray-900 flex items-center gap-2">
                                Plaintes & symptômes <span className="text-sm font-normal text-gray-400">({plaintes.length})</span>
              </h2>
              {hospitalisationActive && (
                <button
                  onClick={() => navigate(`/medecin/suite-soins?consultationId=${consultationId}`)}
                  className="text-xs font-semibold text-clinic-blue hover:text-clinic-blue-dark cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-add-line"></i>
                  Ajouter
                </button>
              )}
            </div>
            <div className="p-5">
              {plaintes.length === 0 ? (
                <p className="text-sm text-gray-400 italic">Aucune plainte enregistrée.</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {plaintes.map((p, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <span className="text-xs font-semibold text-gray-500 whitespace-nowrap flex-shrink-0 mt-0.5 min-w-[80px]">
                        {formatDate(p.date)}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold whitespace-nowrap flex-shrink-0 ${p.source === 'Admission' ? 'bg-clinic-blue/15 text-clinic-blue-dark' : 'bg-blue-100 text-blue-700'}`}>
                        {p.source}
                      </span>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap flex-1">{p.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Soins */}
          <div className={cardClass}>
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-gray-900 flex items-center gap-2">
                                Soins administrés <span className="text-sm font-normal text-gray-400">({soins.length})</span>
              </h2>
              {hospitalisationActive && (
                <button
                  onClick={() => navigate(`/medecin/suite-soins?consultationId=${consultationId}`)}
                  className="text-xs font-semibold text-clinic-blue hover:text-clinic-blue-dark cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-add-line"></i>
                  Ajouter
                </button>
              )}
            </div>
            <div className="p-5">
              {soins.length === 0 ? (
                <p className="text-sm text-gray-400 italic">Aucun soin enregistré.</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {soins.map((s, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-blue-50/40 border border-blue-100 rounded-lg">
                      <span className="text-xs font-semibold text-gray-500 whitespace-nowrap flex-shrink-0 mt-0.5 min-w-[80px]">
                        {formatDate(s.date)}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold whitespace-nowrap flex-shrink-0 ${s.source === 'Fiche de soins' ? 'bg-clinic-blue/15 text-clinic-blue-dark' : 'bg-blue-100 text-blue-700'}`}>
                        {s.source}
                      </span>
                      {s.statut && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold border flex-shrink-0 ${statutBadgeClass(s.statut)}`}>
                          {s.statut}
                        </span>
                      )}
                      <p className="text-sm text-gray-800 whitespace-pre-wrap flex-1">{s.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Bilan labo */}
          <div className={cardClass}>
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-gray-900 flex items-center gap-2">
                                Bilan du laboratoire <span className="text-sm font-normal text-gray-400">({bilanLabo.length})</span>
              </h2>
              {hospitalisationActive && (
                <button
                  onClick={() => navigate(`/medecin/bon-laboratoire?patientId=${patientId}&consultationId=${consultationId}`)}
                  className="text-xs font-semibold text-clinic-blue hover:text-clinic-blue-dark cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-add-line"></i>
                  Ajouter
                </button>
              )}
            </div>
            <div className="p-5">
              {bilanLabo.length === 0 ? (
                <p className="text-sm text-gray-400 italic">Aucun résultat de laboratoire.</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {bilanLabo.map((b, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-clinic-blue/5 border border-clinic-blue/20 rounded-lg">
                      <span className="text-xs font-semibold text-gray-500 whitespace-nowrap flex-shrink-0 mt-0.5 min-w-[80px]">
                        {formatDate(b.date)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{b.analyses}</p>
                        {b.resultats && (
                          <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{b.resultats}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Diagnostic */}
          <div className={cardClass}>
            <div className="p-5 border-b border-gray-100">
              <h2 className="font-bold text-gray-900 flex items-center gap-2">
                                Diagnostic
              </h2>
            </div>
            <div className="p-5">
              {consultation.diagnostic ? (
                <div className="p-4 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-sm text-red-800 font-medium whitespace-pre-wrap">{consultation.diagnostic}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Posé le {entryDate} par Dr {consultation.medecin_nom} {consultation.medecin_prenom}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">Aucun diagnostic enregistré pour cette consultation.</p>
              )}
            </div>
          </div>
        </div>

        {/* Suites de soins détail */}
        {suites.length > 0 && (
          <div className={cardClass + ' mt-6'}>
            <div className="p-5 border-b border-gray-100">
              <h2 className="font-bold text-gray-900 flex items-center gap-2">
                                Suites de soins liées ({suites.length})
              </h2>
            </div>
            <div className="p-5">
              <div className="space-y-3">
                {suites.map((suite) => (
                  <div key={suite.id} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-semibold text-gray-900">
                        Suite du {formatDate(suite.created_at)}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${statutBadgeClass(suite.statut)}`}>
                        {suite.statut === 'en_cours' ? 'En cours' : 'Terminée'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {suite.plaintes.length > 0 && (
                        <div>
                          <p className="text-xs font-bold text-gray-500 uppercase mb-1.5">Plaintes ({suite.plaintes.length})</p>
                          <div className="space-y-1">
                            {suite.plaintes.map((p, i) => (
                              <div key={i} className="text-sm text-gray-800 bg-gray-50 rounded-lg p-2">
                                <span className="text-xs text-gray-500">{formatDate(p.date_plainte)} — </span>
                                {p.description}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {suite.sessions.length > 0 && (
                        <div>
                          <p className="text-xs font-bold text-gray-500 uppercase mb-1.5">Soins ({suite.sessions.length})</p>
                          <div className="space-y-1">
                            {suite.sessions.map((s, i) => (
                              <div key={i} className="text-sm text-gray-800 bg-blue-50 rounded-lg p-2">
                                <span className="text-xs text-gray-500">{formatDate(s.date_soin)} — </span>
                                {s.description}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />

      {/* Modal impression */}
      <PrintSingleDocumentModal
        isOpen={printOpen}
        onClose={() => setPrintOpen(false)}
        documentTitle="Fiche de visite"
      >
        <PrintableFicheVisite
          patientName={`${patient.prenom} ${patient.nom}`}
          patientHeader={patientHeader}
          allergies={patient.allergies || ''}
          age={age}
          sexe={patient.sexe === 'M' ? 'Masculin' : 'Féminin'}
          numeroDossier={patient.numero_dossier || 'N/A'}
          groupeSanguin={patient.groupe_sanguin || ''}
          entryDate={entryDate}
          entryHeure={entryHeure}
          medecinNom={`Dr ${consultation.medecin_nom} ${consultation.medecin_prenom}`}
          medecinSpecialite={consultation.medecin_specialite || ''}
          constantes={constantes}
          antecedents={[
            { label: 'Maladies chroniques', value: patient.maladies_chroniques || '' },
            { label: 'Antécédents médicaux', value: patient.antecedents_medicaux || '' },
            { label: 'Antécédents chirurgicaux', value: patient.antecedents_chirurgicaux || '' },
            { label: 'Médicaments actuels', value: patient.medicaments_actuels || '' },
          ]}
          symptomes={consultation.symptomes_signes || ''}
          examen={consultation.examen || ''}
          plaintes={plaintes}
          soins={soins}
          bilanLabo={bilanLabo}
          diagnostic={consultation.diagnostic || ''}
          practitionerName={practitionerName}
          practitionerTitle={practitionerTitle}
          date={new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
        />
      </PrintSingleDocumentModal>
    </div>
  );
}